import { useState } from "react";

import { UserSubscriptionAction } from "common/types";
import { Optional } from "common/utils/types";

import { useCurrentDialog } from "client/common/hooks/stackables";

import {
  useRecordRetentionOfferDecisionMutation,
  useSubscriptionRetentionOfferQuery,
  useUpdateSubscriptionMutation,
} from "../../hooks";
import {
  DetailSubscription,
  EligibleRetentionOffer,
  ListSubscription,
  RetentionOfferCancelReason,
  RetentionOfferPreview,
  SubscriptionCancelRequest,
} from "../../types";
import { isRetentionOfferCancelReason } from "../../utils";
import SubscriptionCancelReasonStep from "../steps/SubscriptionCancelReasonStep";
import SubscriptionRetentionOfferAcceptedStep from "../steps/SubscriptionRetentionOfferAcceptedStep";
import SubscriptionRetentionOfferStep from "../steps/SubscriptionRetentionOfferStep";

const exhaustiveStepCheck = (step: never) => {
  console.warn("Missing render for cancel dialog step", step);
  return null;
};

const getEligibleRetentionOffer = (
  preview: Optional<RetentionOfferPreview>,
): Optional<EligibleRetentionOffer> => {
  const projectedNextOrderData = preview?.projected_next_order_data;
  if (!preview || !projectedNextOrderData) {
    return undefined;
  }
  return { ...preview, projected_next_order_data: projectedNextOrderData };
};

export type SubscriptionCancelDialogProps = {
  subscription: DetailSubscription | ListSubscription;
};

/**
 * The offer and confirmation steps hold the offer they opened with rather than reading it back off
 * the preview query: accepting refetches that query, which then reports the offer as accepted, so
 * a step reading from it would switch to the accepted offer reminder mid-flow.
 */
type CancelStep =
  | { name: "reason" }
  | {
      name: "offer";
      offer: EligibleRetentionOffer;
      cancelReason: RetentionOfferCancelReason;
    }
  | {
      name: "acceptedOfferReminder";
      offer: EligibleRetentionOffer;
      cancelReason: RetentionOfferCancelReason;
    }
  | { name: "accepted"; offer: EligibleRetentionOffer };

const SubscriptionCancelDialog = ({ subscription }: SubscriptionCancelDialogProps) => {
  const { close } = useCurrentDialog();
  const {
    mutate: updateSubscription,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [step, setStep] = useState<CancelStep>({ name: "reason" });

  const { data: offerPreview, isLoading: isOfferPreviewLoading } =
    useSubscriptionRetentionOfferQuery(subscription.id);
  const {
    mutate: recordOfferDecision,
    isPending: isRecordingDecision,
    variables: offerDecision,
    error: decisionError,
  } = useRecordRetentionOfferDecisionMutation(subscription.id);

  const offer = getEligibleRetentionOffer(offerPreview);

  const cancelSubscription = ({ reason, reasonOther }: SubscriptionCancelRequest) => {
    updateSubscription({
      action: UserSubscriptionAction.Cancel,
      cancel_reason: reason,
      cancel_reason_other: reasonOther,
    });
  };

  const handleReasonConfirm = (cancelRequest: SubscriptionCancelRequest) => {
    if (offer && isRetentionOfferCancelReason(cancelRequest.reason)) {
      const cancelReason = cancelRequest.reason;
      setStep(
        offer.is_accepted
          ? { name: "acceptedOfferReminder", offer, cancelReason }
          : { name: "offer", offer, cancelReason },
      );
      return;
    }
    cancelSubscription(cancelRequest);
  };

  switch (step.name) {
    case "reason":
      return (
        <SubscriptionCancelReasonStep
          onConfirm={handleReasonConfirm}
          onDeny={close}
          isLoading={isUpdating}
          isOfferPreviewLoading={isOfferPreviewLoading}
          error={updateError}
        />
      );
    case "offer":
      return (
        <SubscriptionRetentionOfferStep
          subscription={subscription}
          offer={step.offer}
          onAccept={() =>
            recordOfferDecision(
              { accepted: true, cancel_reason: step.cancelReason },
              {
                onSuccess: () => setStep({ name: "accepted", offer: step.offer }),
              },
            )
          }
          onDecline={() =>
            recordOfferDecision(
              { accepted: false, cancel_reason: step.cancelReason },
              // The client came here to cancel, so cancel even if we could not record the
              // decline. Otherwise a failed record would leave them with no way out
              { onSettled: () => cancelSubscription({ reason: step.cancelReason }) },
            )
          }
          isLoading={isRecordingDecision && offerDecision?.accepted === true}
          isDenyLoading={isUpdating || (isRecordingDecision && offerDecision?.accepted === false)}
          error={decisionError ?? updateError}
        />
      );
    case "acceptedOfferReminder":
      return (
        <SubscriptionRetentionOfferStep
          subscription={subscription}
          offer={step.offer}
          onAccept={close}
          onDecline={() => cancelSubscription({ reason: step.cancelReason })}
          isLoading={false}
          isDenyLoading={isUpdating}
          error={updateError}
        />
      );
    case "accepted":
      return (
        <SubscriptionRetentionOfferAcceptedStep
          subscription={subscription}
          offer={step.offer}
          onDone={close}
        />
      );
    default:
      return exhaustiveStepCheck(step);
  }
};

export default SubscriptionCancelDialog;
